import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Tooltip, Typography } from "@material-ui/core";
import React from "react";
import {
  ExperienceReservation,
  PriceBreakdownLineItem,
  RedeemedEarnOffersResponse,
} from "redmond";
import {
  getPriceString,
  getTotalPriceText,
  Icon,
  IconName,
  quickPluralize,
  twoDecimalFormatter,
} from "halifax";
import { formatPaymentBreakdown, ILineItem } from "../../../FlightCard/helpers";
import "./styles.scss";
import { getCurrencyString } from "../../../../../../../../utils/helpers";
import {
  CHECKOUT_BREAKDOWN,
  FEES_DUE_UPON_ARRIVAL,
  EXPERIENCE_TOTAL,
  MANDATORY_FEES_BELOW,
  MANDATORY_FEES_TOOLTIP_TEXT,
  PAID_AT_TIME_OF_BOOKING,
  TAXES_AND_FEES_INCLUDED,
} from "./constants";

interface IExperiencePaymentModalContentProps {
  experienceReservation: ExperienceReservation;
  redeemedEarnOffers: RedeemedEarnOffersResponse["redeemedEarnOffers"];
}

export const ExperiencePaymentModalContent = ({
  experienceReservation,
  redeemedEarnOffers,
}: IExperiencePaymentModalContentProps) => {
  const { reservation, paymentBreakdown } = experienceReservation;

  const lineItems = formatPaymentBreakdown(paymentBreakdown);

  const renderTravelerPriceBreakdown = () => {
    if (!reservation?.bookingSession) return null;

    const { priceBreakdown } = reservation.bookingSession;

    const priceItems = priceBreakdown.map(
      ({ specificAgeBand, totalPriceForAgeBand }: PriceBreakdownLineItem) => ({
        label: `${getPriceString({
          price: specificAgeBand.pricePerTraveler.fiat.value,
          currencySymbol: specificAgeBand.pricePerTraveler.fiat.currencySymbol,
        })} x ${specificAgeBand.numberOfTravelers} ${quickPluralize(
          specificAgeBand.numberOfTravelers,
          specificAgeBand.ageBand.toLowerCase(),
        )}`,
        amount: totalPriceForAgeBand.fiat,
      }),
    );

    return (
      <Box className="experience-payment-container">
        <Box className="experience-payment-details-container">
          {priceItems.map((priceItem) => (
            <Box className="experience-pricing-row">
              <Typography variant="body2">{priceItem.label}</Typography>
              <Typography variant="body2">
                {getTotalPriceText({
                  price: priceItem.amount,
                  priceFormatter: twoDecimalFormatter,
                })}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderFeeDueUponArrival = () => {
    if (
      !reservation?.experienceDetails?.feesDueUponArrival ||
      !reservation?.bookingSession?.totalSellAtPriceWithFees
    )
      return null;

    const { totalSellAtPriceWithFees } = reservation.bookingSession;
    const { mandatoryExtraChargeItems, totalMandatoryExtraCharges } =
      reservation.experienceDetails.feesDueUponArrival;
    const { currencyCode, currencySymbol } = totalSellAtPriceWithFees.fiat;

    return (
      <>
        <Box className="subtotal-row">
          <Box className="left">
            <Typography className="subtotal-text" variant="body2">
              {PAID_AT_TIME_OF_BOOKING}
            </Typography>
            <Tooltip
              title={`${TAXES_AND_FEES_INCLUDED} ${MANDATORY_FEES_BELOW}`}
              tabIndex={0}
              enterTouchDelay={0} // Show tooltip immediately on touch
              leaveTouchDelay={5000} // Keep open longer
              classes={{
                tooltip: "consistent-tooltip-style",
              }}
            >
              <Box
                className="subtotal-tooltip"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                aria-label="Price information"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Icon name={IconName.InfoCircle} />
              </Box>
            </Tooltip>
          </Box>
          <Box className="right">
            <Typography variant="body2">
              {getCurrencyString({
                currencyCode,
                currencySymbol,
                value: totalSellAtPriceWithFees.fiat.value,
              })}
            </Typography>
          </Box>
        </Box>
        <Box className="fees-upon-arrival-details-container">
          {mandatoryExtraChargeItems.map((item) => (
            <Box className="experience-pricing-row">
              <Typography variant="body2">{item.chargeDescription}</Typography>
              <Typography className="nowrap" variant="body2">
                {/* {getCurrencyString({
                  currencyCode,
                  currencySymbol,
                  value: item.chargeInDestinationCurrency.totalExtraCharges,
                })} */}
                {getTotalPriceText({
                  price: {
                    currencyCode,
                    currencySymbol,
                    value: item.chargeInDestinationCurrency.totalExtraCharges,
                  },
                  priceFormatter: twoDecimalFormatter,
                })}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box className="subtotal-row">
          <Box className="left">
            <Typography variant="body2">{FEES_DUE_UPON_ARRIVAL}</Typography>
            <Tooltip
              title={MANDATORY_FEES_TOOLTIP_TEXT}
              tabIndex={0}
              enterTouchDelay={0} // Show tooltip immediately on touch
              leaveTouchDelay={5000} // Keep open longer
              classes={{
                tooltip: "consistent-tooltip-style",
              }}
            >
              <Box
                className="subtotal-tooltip"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                aria-label="Price information"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Icon name={IconName.InfoCircle} />
              </Box>
            </Tooltip>
          </Box>
          <Box className="right">
            <Typography variant="body2">
              {getCurrencyString({
                currencyCode,
                currencySymbol,
                value: totalMandatoryExtraCharges,
              })}
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  const renderTripTotal = () => {
    if (!reservation?.bookingSession?.totalSellAtPriceWithFees) return null;

    const { totalSellAtPriceWithFees } = reservation.bookingSession;
    const { feesDueUponArrival } = reservation.experienceDetails || {};

    return (
      <Box className="trip-total-row">
        <Box className="left">
          <Typography
            variant="body1"
            className="trip-total-text"
            component="span"
          >
            {EXPERIENCE_TOTAL}
          </Typography>
          {!feesDueUponArrival && (
            <Tooltip
              title={TAXES_AND_FEES_INCLUDED}
              tabIndex={0}
              enterTouchDelay={0} // Show tooltip immediately on touch
              leaveTouchDelay={5000} // Keep open longer
              classes={{
                tooltip: "consistent-tooltip-style",
              }}
            >
              <Box
                className="price-breakdown-tooltip"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                aria-label="Price information"
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <Icon name={IconName.InfoCircle} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box className="right">
          <Typography variant="body1" className="trip-total-text">
            {getCurrencyString({
              currencyCode: totalSellAtPriceWithFees.fiat.currencyCode,
              currencySymbol: totalSellAtPriceWithFees.fiat.currencySymbol,
              value: feesDueUponArrival
                ? feesDueUponArrival.totalPriceWithMandatoryExtraCharges
                : totalSellAtPriceWithFees.fiat.value,
            })}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderRewards = () => {
    const { rewards } = lineItems;
    if (!rewards) {
      return null;
    }
    return (
      <Box className="rewards-container">
        <Typography variant="body2">{rewards.label}</Typography>
        <Typography variant="body1" className="rewards-points">
          {rewards.amount}
        </Typography>
      </Box>
    );
  };

  const renderPayment = () => {
    const { userCard } = lineItems;
    if (!userCard) {
      return null;
    }
    return (
      <Box className="payment-card-container">
        <Box className="payment-card-details-container">
          <FontAwesomeIcon icon={faCreditCard} className="credit-card-icon" />
          <Typography variant="body2" className="payment-card-details">
            {userCard.label}
          </Typography>
        </Box>
        <Typography variant="body1" className="payment-card-amount">
          {userCard.amount}
        </Typography>
      </Box>
    );
  };

  const renderTravelWallet = (
    travelWallet: ILineItem | null,
    icon: IconName,
  ) => {
    if (!travelWallet) return null;
    return (
      <Box className="offers-container">
        <Box className="offers-icon-label">
          <Icon name={icon} />
          <Typography variant="body2">{travelWallet.label}</Typography>
        </Box>
        <Typography variant="body1" className="offer-amount">
          {travelWallet.amount}
        </Typography>
      </Box>
    );
  };

  const renderOffers = () =>
    renderTravelWallet(lineItems.travelWalletOffer, IconName.OfferTag);

  const renderCredits = () =>
    lineItems.travelWalletCredit?.map((credit) =>
      renderTravelWallet(credit, IconName.PiggyBank),
    );

  const renderEarnOffer = () => (
    <Box className="earn-offer-container">
      {redeemedEarnOffers?.map((offer) => (
        <Box
          className="earn-offer-banner"
          key={`experience-itinerary-earn-offer-${offer.redeemedForOrder.cartOrderId}`}
        >
          <Icon name={IconName.CheckCircleFilledGreen} />
          <Typography className="earn-offer-banner-text">
            {offer.redeemedDescription}
          </Typography>
        </Box>
      ))}
    </Box>
  );
  return (
    <Box className="experience-payment-modal-content">
      <Box className="experience-payment-title-container">
        <Typography variant="body1">{CHECKOUT_BREAKDOWN}</Typography>
      </Box>
      {renderTravelerPriceBreakdown()}
      {renderFeeDueUponArrival()}
      {renderTripTotal()}
      {renderOffers()}
      {renderCredits()}
      {renderRewards()}
      {renderPayment()}
      {renderEarnOffer()}
    </Box>
  );
};
